import { ArrowRight, Bike, Clock, Hamburger, ShoppingBasket, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCart } from "../../context/CartContext";
import { useStoreStatus } from "../../hooks/useStoreStatus";
import { cn, formatPrice } from "../../utils/format";
import { Button } from "../ui/Button";
import { ModalCloseButton } from "../ui/Modal";
import { QuantitySelector } from "../ui/QuantitySelector";

interface CartPanelProps {
  /** id do título (para aria-labelledby) — único por instância. */
  titleId: string;
  /** Exibe o botão de fechar (versão drawer). */
  onClose?: () => void;
  className?: string;
}

/** Conteúdo do carrinho — usado na barra lateral do cardápio e no drawer. */
export const CartPanel = ({ titleId, onClose, className }: CartPanelProps) => {
  const { items, count, subtotal, incrementItem, decrementItem, removeItem, openCheckout } = useCart();
  const storeStatus = useStoreStatus();

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="flex items-center justify-between gap-3 border-b border-line p-5">
        <h2 id={titleId} className="flex items-center gap-2.5 font-display text-3xl tracking-wide">
          <ShoppingBasket className="size-6 text-primary" aria-hidden="true" />
          Meu <span className="text-primary">Pedido</span>
        </h2>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">
            {count} {count === 1 ? "item" : "itens"}
          </span>
          {onClose && <ModalCloseButton onClick={onClose} />}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5" data-lenis-prevent>
        {items.length === 0 ? (
          <div className="flex h-full min-h-48 flex-col items-center justify-center text-center">
            <span className="mb-4 grid size-16 place-items-center rounded-full bg-primary-soft text-primary">
              <Hamburger className="size-8" aria-hidden="true" />
            </span>
            <p className="font-semibold">Sua mochila está vazia!</p>
            <span className="mt-1 text-sm text-muted">Adicione algo do cardápio.</span>
          </div>
        ) : (
          <ul className="space-y-3">
            <AnimatePresence initial={false}>
              {items.map((item) => (
                <motion.li
                  key={item.lineId}
                  layout
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.22 }}
                  className="flex items-start gap-3 rounded-2xl border border-line bg-card p-3"
                >
                  <img
                    src={item.image_url || "/favicon.png"}
                    alt=""
                    className="size-16 shrink-0 rounded-xl object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-semibold">{item.name}</h3>
                    {item.customization && item.is_builder && (
                      <p className="mt-1 text-[11px] leading-snug text-muted">
                        {item.customization.extras.map((e) => (e.quantity > 1 ? `${e.quantity}x ${e.name}` : e.name)).join(" · ")}
                        {item.customization.notes && <span className="block italic">“{item.customization.notes}”</span>}
                      </p>
                    )}
                    {item.customization && !item.is_builder && (
                      <ul className="mt-1 space-y-0.5 text-[11px] leading-snug">
                        {item.customization.removed.length > 0 && (
                          <li className="text-danger">
                            Sem: {item.customization.removed.map((r) => r.name).join(", ")}
                          </li>
                        )}
                        {item.customization.extras.map((extra) => (
                          <li key={extra.id} className="text-success">
                            + {extra.quantity}x {extra.name} ({formatPrice(extra.price * extra.quantity)})
                          </li>
                        ))}
                        {item.customization.notes && (
                          <li className="italic text-muted">“{item.customization.notes}”</li>
                        )}
                      </ul>
                    )}
                    <p className="mt-1 text-xs text-muted">
                      {item.quantity}x {formatPrice(item.unitPrice)} ={" "}
                      <strong className="text-white">{formatPrice(item.unitPrice * item.quantity)}</strong>
                    </p>
                    <QuantitySelector
                      size="sm"
                      className="mt-2"
                      quantity={item.quantity}
                      label={item.name}
                      onIncrement={() => incrementItem(item.lineId)}
                      onDecrement={() => decrementItem(item.lineId)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.lineId)}
                    aria-label={`Remover ${item.name} do carrinho`}
                    title="Remover item"
                    className="grid size-9 shrink-0 place-items-center self-start rounded-full text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>

      <div className="border-t border-line p-5">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between text-muted">
            <dt>Subtotal</dt>
            <dd>{formatPrice(subtotal)}</dd>
          </div>
          <div className="flex justify-between text-muted">
            <dt className="flex items-center gap-2">
              <Bike className="size-4" aria-hidden="true" /> Entrega
            </dt>
            <dd className="font-semibold text-success">Grátis</dd>
          </div>
          <div className="flex justify-between pt-2 text-base font-bold">
            <dt>Total</dt>
            <dd className="text-primary">{formatPrice(subtotal)}</dd>
          </div>
        </dl>
        {/* Trava de horário: dá para montar o pedido, mas só finalizar com a loja aberta. */}
        {!storeStatus.isOpen && (
          <p role="status" className="mt-4 flex items-start gap-2 rounded-xl bg-danger/10 px-3 py-2.5 text-xs text-danger">
            <Clock className="mt-px size-4 shrink-0" aria-hidden="true" />
            {storeStatus.message}. Pedidos online só no horário de funcionamento.
          </p>
        )}
        <Button
          size="lg"
          className="mt-5 w-full"
          disabled={items.length === 0 || !storeStatus.isOpen}
          onClick={openCheckout}
          icon={storeStatus.isOpen ? <ArrowRight className="size-5" aria-hidden="true" /> : undefined}
        >
          {storeStatus.isOpen ? "Finalizar Pedido" : "Loja fechada"}
        </Button>
      </div>
    </div>
  );
};
